import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle, useContext } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useFullScreen } from 'react-browser-hooks'
/* --------------------------------------------- */
import { routeMap } from '../route'
/* --------------------------------------------- */
import Player from '../components/player/Player'
import withRouterInnerRef from '../components/hoc/withRouterInnerRef'
/* --------------------------------------------- */
import { GlobalContext } from '../context/global.context'
import PlayerProvider from '../context/player.context'
import { ErrorContext } from '../context/error.context'
/* --------------------------------------------- */
import ErrorPage from '../components/errors/ErrorPage'
/* --------------------------------------------- */
import useAnalytics from '../containers/useAnalytics'
import useLoggly from '../containers/useLoggly'
/* --------------------------------------------- */
import useDetailsApi from '../apis/useDetailsApi'
/* --------------------------------------------- */
import utils from '../utils/utils'
import useErrorUtils from '../utils/useErrorUtils'
import { getSelectedSubtitleTrack } from '../utils/localStorageService'
// import { useEffectDebugger } from '../utils/debugger'
/* --------------------------------------------- */
// Container component which holds player logic.
const PlayerContainer = forwardRef(({
  mediaInfo = {}, isFullPagePlayer, onErrorOkClick, onPlayClick, thisIsChannel, catalogTheme
}, ref) => {
  const {
    smilUrl, title, subTitle, isLivePlayer, thumbnailImgs,
    mediaId, seriesId, type, episodeNum, seasonNumber, seriesTitle, curentSeason, seasonUid, activeSeasonNumber,
    isSportingEvent, isOnNow, contentGuid, mediaGuid, contentOfferId, assetDetailsForRelatedRailInPlayer, uid
  } = mediaInfo || {}
  // const [isMuxLoading] = useScript({ src: '/static/js/mux.min.js', checkForExisting: true })  // used by shaka to play hls streams.
  // const [isShakaModuleLoading] = useScript({ src: '/static/js/shaka-player.compiled-3.0.7-modified.js', checkForExisting: true })
  // const [isShakaMuxLoading] = useScript({ src: '/static/js/shakaplayer-mux-4.0.1.js', checkForExisting: true })
  /* --------------------------------------------- */
  const playerFeature = useSelector(store => store.featuresReducers.playerFeature)
  const playerTheme = useSelector(store => store.themeConfigReducers.playerTheme)
  const appMessages = useSelector(store => store.appConfigReducers.appMessages)
  const appConfig = useSelector(store => store.appConfigReducers.appConfig)
  const { supportConfig } = useSelector(store => store.support)
  /* --------------------------------------------- */
  const { fullScreen: isFullscreen, close: closeFullscreen } = useFullScreen()
  /* --------------------------------------------- */
  if (!supportConfig.errorConfiguration) supportConfig.errorConfiguration = []
  /* --------------------------------------------- */
  const location = useLocation()
  const history = useHistory()
  /* --------------------------------------------- */
  const { log } = useLoggly()
  /* --------------------------------------------- */
  const { trailer } = utils.parse(location.search)
  /* --------------------------------------------- */
  const localSelectedSubTitle = getSelectedSubtitleTrack()
  /* --------------------------------------------- */
  const playerRef = useRef(null)
  const portraitPlayerRef = useRef(false)
  const progressTimer = useRef(null)
  const currentTime = useRef(0)
  const durationTime = useRef(0)
  const isTrailerRef = useRef(!!trailer)
  const subtitleRef = useRef((!localSelectedSubTitle.language || localSelectedSubTitle.language === 'none') ? 'off' : localSelectedSubTitle.language)
  const pidRef = useRef('')
  const nextEpisodeRef = useRef(null)
  const errorConfigurationRef = useRef(supportConfig.errorConfiguration)
  const completeRef = useRef(false)
  const mediaInfoRef = useRef(mediaInfo)
  const releaseUrlRef = useRef(smilUrl)
  const exitFullscreenRef = useRef(null)
  const isFullscreenRef = useRef(false)
  /* --------------------------------------------- */
  const [shakaConfig, setShakaConfig] = useState({})
  // const [isAutoPlay, setIsAutoPlay] = useState(false)
  const [playerStartTime] = useState(Date.now())
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [bingeCountdownDuration, setBingeCountdownDuration] = useState(0)
  const [isShowError, setShowError] = useState(false)
  const [isPlayerLoaded, setPlayerLoaded] = useState(false)
  /* --------------------------------------------- */
    /* --------------------------------------------- */
  const currentPlayerMediaDataRef = useRef({
    seriesTitle,
    isLivePlayer,
    type,
    duration,
    isTrailer: isTrailerRef.current
  })
  /* --------------------------------------------- */
  const { isAuthenticated, isAutoplay: isGlobalAutoPlay } = useContext(GlobalContext)
  const {
    errorPage, errorPopup, errorFullscreen, errorToast, errorSnackbar
  } = useContext(ErrorContext)
  /* --------------------------------------------- */
  const isAuthenticatedRef = useRef(isAuthenticated)
  const isGlobalAutoPlayRef = useRef(isGlobalAutoPlay)
  /* --------------------------------------------- */
  const { sendAnalytics } = useAnalytics()
  const { sendMediaProgress } = useDetailsApi()
  /* --------------------------------------------- */
  const { showError } = useErrorUtils()
  /* --------------------------------------------- */
  const handleErrorOkClick = useCallback(() => {
    if (onErrorOkClick && !playerFeature.portraitPlayerEnabled) onErrorOkClick()
  }, [onErrorOkClick, playerFeature.portraitPlayerEnabled])
  /* --------------------------------------------- */
  useEffect(() => {
    releaseUrlRef.current = smilUrl
  }, [smilUrl])
  /* --------------------------------------------- */
  useEffect(() => {
    portraitPlayerRef.current = playerFeature.portraitPlayerEnabled
  }, [playerFeature.portraitPlayerEnabled])
  /* --------------------------------------------- */
  useEffect(() => {
    mediaInfoRef.current = mediaInfo
  }, [mediaInfo])
  /* --------------------------------------------- */
  useEffect(() => {
    errorConfigurationRef.current = supportConfig.errorConfiguration || []
  }, [supportConfig.errorConfiguration])
  /* --------------------------------------------- */
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated
  }, [isAuthenticated])
  /* --------------------------------------------- */
  useEffect(() => {
    isGlobalAutoPlayRef.current = isGlobalAutoPlay
  }, [isGlobalAutoPlay])
  /* --------------------------------------------- */
  /**
   * Callback to exit fullscreen
   */
  exitFullscreenRef.current = useCallback(() => {
    closeFullscreen()
  }, [closeFullscreen])
  /* --------------------------------------------- */
  const setNextEpisode = useCallback((nextEpisode) => {
    nextEpisodeRef.current = nextEpisode
  }, [])
  /* --------------------------------------------- */
  const getReleaseUrl = useCallback((releaseUrl) => {
    return releaseUrlRef.current
  }, [])
  /* --------------------------------------------- */
  const setReleaseUrl = useCallback((releaseUrl) => {
    releaseUrlRef.current = releaseUrl
  }, [])
  /* --------------------------------------------- */
  const getPlayerUIConfig = useCallback(() => {
    if (playerFeature.portraitPlayerEnabled) {
      if (isLivePlayer) {
        return {
          fullscreen: { ...playerFeature.fullScreenConfig, ...playerFeature.linearPlayerConfig },
          nonFullscreen: { ...playerFeature.portraitConfig, ...playerFeature.linearPlayerConfig }
        }
      } else {
        return {
          fullscreen: playerFeature.fullScreenConfig,
          nonFullscreen: playerFeature.portraitConfig
        }
      }
    } else {
      if (isLivePlayer) {
        return {
          fullscreen: { ...playerFeature.fullScreenConfig, ...playerFeature.linearPlayerConfig },
          nonFullscreen: { ...playerFeature.fullScreenConfig, ...playerFeature.linearPlayerConfig }
        }
      } else {
        return {
          fullscreen: playerFeature.fullScreenConfig || {},
          nonFullscreen: playerFeature.fullScreenConfig || {}
        }
      }
    }
  }, [playerFeature, isLivePlayer])
  /* --------------------------------------------- */
  const getPlayerUITheme = useCallback(() => {
    if (playerFeature.portraitPlayerEnabled) {
      if (isLivePlayer) {
        return {
          fullscreen: { ...playerTheme.fullScreenTheme, ...playerTheme.linearPlayerTheme },
          nonFullscreen: { ...playerTheme.portraitTheme, ...playerTheme.linearPlayerTheme }
        }
      } else {
        return {
          fullscreen: playerTheme.fullScreenTheme,
          nonFullscreen: playerTheme.portraitTheme
        }
      }
    } else {
      if (isLivePlayer) {
        return {
          fullscreen: { ...playerTheme.fullScreenTheme }, // revert back
          nonFullscreen: { ...playerTheme.fullScreenTheme }
        }
      } else {
        return {
          fullscreen: playerTheme.fullScreenTheme,
          nonFullscreen: playerTheme.fullScreenTheme
        }
      }
    }
  }, [playerFeature, playerTheme, isLivePlayer])
  /* --------------------------------------------- */
  const onFullscreenChange = useCallback((isFullscreen) => {
    isFullscreenRef.current = isFullscreen ? true : false
    const playerUIConfig = getPlayerUIConfig();
    /* --------------------------------------------- */
    if (!playerRef.current) return
    /* --------------------------------------------- */
    const config = isFullscreen ? playerUIConfig.fullscreen : playerUIConfig.nonFullscreen
    playerRef.current.showBackBtn(config.backButton)
    playerRef.current.showBingeWatching(config.bingeWatching)
    playerRef.current.showBreadCrumbView(config.breadCrumbView)
    playerRef.current.setForwardDuration(config.fwdDuration)
    playerRef.current.showMuteUnmuteBtn(config.muteButton)
    playerRef.current.showPlayPauseBtn(config.playPauseButton)
    playerRef.current.showQualitySelectorBtn(config.qualitySelector)
    playerRef.current.setPlaybackAssetFormat(config.playbackAssetFormat || 'MPEG-DASH,M3U')
    playerRef.current.setPlaybackPreviewAssetFormat(config.previewAssetFormat)
    playerRef.current.setRewindDuration(config.rewDuration)
    playerRef.current.showSeekBar(config.seekBar)
    playerRef.current.showSeekPreview(config.seekPreview)
    playerRef.current.showTitle(config.titleView)
    playerRef.current.showSubTitleSelectorBtn(config.subtitleSelector)
    playerRef.current.showFullscreenBtn(config.orientationSwitch || isFullPagePlayer)
    setBingeCountdownDuration(config.bingeCountdownDuration)
      playerRef.current.showRewindBtn(config.rewButton)
      playerRef.current.showForwardBtn(config.fwdButton)
      playerRef.current.showRemainingTime(config.remainingTime)
      playerRef.current.showElapsedTime(config.elapsedTime)
      playerRef.current.showTotalDuration(config.totalDuration)
    /* --------------------------------------------- */
    const playerUITheme = getPlayerUITheme()
    const theme = isFullscreen ? playerUITheme.fullscreen : playerUITheme.nonFullscreen
    playerRef.current.setPlayerTheme(theme)
  }, [getPlayerUIConfig, getPlayerUITheme, isFullPagePlayer])
  /* --------------------------------------------- */
  const onPlaceholderPlayBtnClick = useCallback((isTrailer, props = {}, details = {}) => {
    const { progress = 0, duration = 0 } = props || {}
    if (!playerRef.current) return
    if (playerFeature.portraitPlayerEnabled || isFullPagePlayer) {
      // playerRef.current.autoPlay(true)
      // setIsAutoPlay(true)
      playerRef.current.onPlayClick()
    } else {
      isTrailerRef.current = isTrailer
      if (isTrailerRef.current) history.push(`${routeMap.player}?mediaId=${mediaId}&trailer=true&season=${activeSeasonNumber}&progress=${progress}&duration=${duration}`, { details })
      else history.push(`${routeMap.player}?mediaId=${mediaId}&progress=${progress}&duration=${duration}`, { details })
    }
  }, [history, mediaId, isFullPagePlayer, playerFeature.portraitPlayerEnabled, activeSeasonNumber])
  /* --------------------------------------------- */
  const sendProgress = useCallback(({ enableNotification } = {}) => {
    if (isTrailerRef.current) return
    if (!playerFeature.playFromLastPosition) return
    const progress = parseInt(currentTime.current) * 1000 || 0
    const duration = parseInt(durationTime.current) * 1000 || 0
    let curentSeasonUid = ''
    if (curentSeason) curentSeasonUid = curentSeason
    else curentSeasonUid = seasonUid
    if (isAuthenticatedRef.current && mediaInfoRef?.current?.type !== 'live') {
      sendMediaProgress(appConfig.baseUrl, {
        ...mediaInfoRef.current,
        mediaId,
        progress,
        duration,
        seriesUid: seriesId,
        seasonUid: curentSeasonUid,
        seriesTitle,
        title,
        uid: mediaId,
        streams: smilUrl,
        seasonNumber,
        episodeNumber: episodeNum,
        type: mediaInfoRef.current?.type,
        nextEpisode: nextEpisodeRef.current || null
      }, enableNotification).catch(() => { })
    }
  }, [
    // type,
    title,
    mediaId,
    smilUrl,
    seriesId,
    appConfig.baseUrl,
    sendMediaProgress,
    curentSeason,
    seasonUid,
    seasonNumber,
    episodeNum,
    seriesTitle,
    playerFeature.playFromLastPosition
  ])
  /* --------------------------------------------- */
  const onStart = useCallback(() => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendProgress({ enableNotification: 'false' })
    sendAnalytics({
      type: '_content.playback',
      action: 'start',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        content_duration: duration,
        progress,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion,
      }
    })
    log({
      type: 'Info',
      attr: {
        event: 'playback',
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'start',
          source_url: releaseUrl,
          title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
          language_code: subtitle,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        }
      }
    })
  }, [
    playerStartTime,
    type,
    title,
    mediaGuid,
    contentGuid,
    sendAnalytics,
    contentOfferId,
    isOnNow,
    log,
    playerFeature.portraitPlayerEnabled,
    sendProgress,
    mediaInfo.recommendation_id,
    mediaInfo.uid,
  ])
  /* --------------------------------------------- */
  const onPIDChange = useCallback((pid) => {
    pidRef.current = pid
  }, [])
  /* --------------------------------------------- */
  const onUserPlayClick = useCallback(() => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'resume',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        content_duration: duration,
        progress,
        portion,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid
      }
    })
  }, [
    contentGuid, contentOfferId, playerFeature.portraitPlayerEnabled,
    playerStartTime, sendAnalytics, title, type, mediaInfo.recommendation_id,  mediaInfo.uid
  ])
  /* --------------------------------------------- */
  const onPlay = useCallback(() => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const releaseUrl = releaseUrlRef.current
    log({
      type: 'Info',
      attr: {
        event: 'playback',
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'resume',
          source_url: releaseUrl,
          title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          item_id: contentGuid,
          content_duration: duration,
          progress: progress,
          playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        }
      }
    })
    if (playerFeature.progressSaveInterval) clearInterval(progressTimer.current)
    if (playerFeature.progressSaveInterval) progressTimer.current = setInterval(sendProgress, 1 * playerFeature.progressSaveInterval * 1000)
  }, [sendProgress, title, playerFeature.progressSaveInterval, log, contentGuid, isOnNow,
    playerFeature.portraitPlayerEnabled, mediaGuid, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onUserPauseClick = useCallback(() => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'pause',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        content_duration: duration,
        progress,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
  }, [
    contentGuid, contentOfferId, playerFeature.portraitPlayerEnabled,
    playerStartTime, sendAnalytics, title, type, mediaInfo.recommendation_id, mediaInfo.uid
  ])
  /* --------------------------------------------- */
  const onPause = useCallback(() => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const releaseUrl = releaseUrlRef.current
    log({
      type: 'Info',
      attr: {
        event: 'playback',
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'pause',
          source_url: releaseUrl,
          title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          item_id: contentGuid,
          content_duration: duration,
          progress: progress,
          playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
          language_code: subtitle,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        }
      }
    })
    clearInterval(progressTimer.current)
    sendProgress()
  }, [sendProgress, title, log, contentGuid, isOnNow,
    playerFeature.portraitPlayerEnabled, mediaGuid, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onCurrentTimeChange = useCallback((cTime, dTime) => {
    currentTime.current = cTime / 1000
    durationTime.current = dTime / 1000
  }, [])
  /* --------------------------------------------- */
  const onComplete = useCallback(() => {
    completeRef.current = true
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'complete',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Info',
      attr: {
        event: 'playback',
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'complete',
          source_url: releaseUrl,
          title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          content_duration: duration,
          playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        }
      }
    })
    sendProgress({ enableNotification: 'true' })
    if (!playerFeature.portraitPlayerEnabled &&
      (((type !== 'episode' && type !== 'series') ||
        isTrailerRef.current ||
        !nextEpisodeRef.current ||
        !isGlobalAutoPlayRef.current))) {
          isFullscreenRef.current && exitFullscreenRef.current() // Exit fullscreen when media finishes playback
          history.goBack()
    }
  }, [sendAnalytics, title, type, log, isOnNow, history, contentOfferId, playerStartTime,
    playerFeature.portraitPlayerEnabled, contentGuid, mediaGuid, sendProgress, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onUnload = useCallback(() => {
    if (completeRef.current) return
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'stop',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        content_duration: duration,
        progress,
        portion,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
      }
    })
    sendProgress({ enableNotification: 'true' })
    log({
      type: 'Info',
      attr: {
        event: 'playback',
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'stop',
          source_url: releaseUrl,
          title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          portion,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
        }
      }
    })
  }, [sendAnalytics, title, type, sendProgress, contentOfferId, log, isOnNow, playerStartTime,
    playerFeature.portraitPlayerEnabled, contentGuid, mediaGuid, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onSeek = useCallback((seekTime) => {
    const progress = parseInt(seekTime / 1000)
    const duration = parseInt(durationTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'seek',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Info',
      attr: {
        event: 'playback',
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'seek',
          source_url: releaseUrl,
          title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          content_duration: duration,
          progress,
          playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        }
      }
    })
    currentTime.current = progress
    sendProgress()
  }, [sendAnalytics, title, type, contentOfferId, isOnNow, log, playerStartTime,
    playerFeature.portraitPlayerEnabled, contentGuid, mediaGuid, sendProgress, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onBuffer = useCallback(() => {
    const _duration = parseInt(durationTime.current) || parseInt(duration / 1000)
    const _progress = parseInt(currentTime.current) || parseInt(progress / 1000)

    const portion = _progress / _duration
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'buffer',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        content_duration: _duration,
        progress: _progress,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Info',
      attr: {
        event: 'playback',
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'buffer',
          source_url: releaseUrl,
          title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          content_duration: duration,
          progress,
          playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        }
      }
    })
  }, [sendAnalytics, title, type, contentGuid, mediaGuid, contentOfferId, isOnNow, log,
    playerFeature.portraitPlayerEnabled, playerStartTime, duration, progress, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onConcurrencyLimitViolation = useCallback((errorInfo) => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === 'PlaybackConcurrency'
    })[0] || {}
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: 'PlaybackConcurrency',
        error_code: config.code,
        module: 'Concurrency',
        event: '_content.playback',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown: true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
  }, [sendAnalytics, title, type, contentGuid, mediaGuid, contentOfferId, isOnNow,
    playerFeature.portraitPlayerEnabled, log, playerStartTime, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onGeoLocationBlocked = useCallback((errorInfo) => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === 'PlaybackGeoRestricted'
    })[0] || {}
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: 'PlaybackGeoRestricted',
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
  }, [sendAnalytics, title, type, contentGuid, mediaGuid, contentOfferId, isOnNow,
    playerFeature.portraitPlayerEnabled, log, playerStartTime, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onPlayerVpnBlocked = useCallback((errorInfo) => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === 'PlayerVpnBlocked'
    })[0] || {}
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: 'PlayerVpnBlocked',
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
  }, [sendAnalytics, title, type, contentGuid, mediaGuid, contentOfferId, isOnNow,
    playerFeature.portraitPlayerEnabled, log, playerStartTime, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onLicenseNotGranted = useCallback((errorInfo) => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === 'PlaybackEntitlement'
    })[0] || {}
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: 'PlaybackLicenseNotGranted',
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
  }, [sendAnalytics, title, type, contentGuid, mediaGuid, contentOfferId, isOnNow,
    playerFeature.portraitPlayerEnabled, log, playerStartTime, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onUnknowFormat = useCallback((errorInfo) => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === 'PlaybackFormat'
    })[0] || {}
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: 'PlaybackFormat',
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
  }, [sendAnalytics, title, type, contentGuid, mediaGuid, contentOfferId, isOnNow,
    playerFeature.portraitPlayerEnabled, log, playerStartTime, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onAccessDenied = useCallback((errorInfo) => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === 'PlaybackFormat'
    })[0] || {}
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: 'PlaybackFormat',
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
  }, [sendAnalytics, title, type, contentGuid, mediaGuid, contentOfferId, isOnNow,
    playerFeature.portraitPlayerEnabled, log, playerStartTime, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onPlaybackDRMError = useCallback((errorInfo) => {
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    const errorType = 'PlaybackDRM'
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === errorType
    })[0] || {}
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: errorType,
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
    showError({
      type: errorType,
      value: {
        onPrimaryBtnClick: handleErrorOkClick,
        isPlayerError: true,
        isPortraitPlayer: portraitPlayerRef.current
      },
      errorInfo,
      isSendLog: false
    })
  }, [sendAnalytics, title, type, contentGuid, mediaGuid, contentOfferId, isOnNow,
    playerFeature.portraitPlayerEnabled, log, playerStartTime, handleErrorOkClick, showError, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onPlaybackGenericError = useCallback((errorInfo) => {
    const errorType = 'PlaybackGeneric'
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === errorType
    })[0] || {}
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: errorType,
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
    showError({
      type: errorType,
      value: {
        onPrimaryBtnClick: handleErrorOkClick,
        isPlayerError: true,
        isPortraitPlayer: portraitPlayerRef.current
      },
      errorInfo,
      isSendLog: false
    })
  }, [sendAnalytics, title, type, playerFeature.portraitPlayerEnabled, contentOfferId, isOnNow,
    contentGuid, mediaGuid, log, playerStartTime, handleErrorOkClick, showError, mediaInfo.recommendation_id, mediaInfo.uid])
 /* --------------------------------------------- */
  const onPlaybackManifestFailed = useCallback((errorInfo) => {
    const errorType = 'ManifestFailed'
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === errorType
    })[0] || {}
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: errorType,
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
    showError({
      type: errorType,
      value: {
        onPrimaryBtnClick: handleErrorOkClick,
        isPlayerError: true,
        isPortraitPlayer: portraitPlayerRef.current
      },
      errorInfo,
      isSendLog: false
    })
  }, [sendAnalytics, title, type, playerFeature.portraitPlayerEnabled, contentOfferId, isOnNow,
    contentGuid, mediaGuid, log, playerStartTime, handleErrorOkClick, showError, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onRecoverablePlayerError = useCallback((errorInfo) => {
    const releaseUrl = releaseUrlRef.current
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    log({
      type: 'Info',
      attr: {
        error_type: "RecoverablePlayerError",
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          portion
        },
        errorInfo
      }
    })
  }, [title, isOnNow, contentGuid, mediaGuid, log])
   /* --------------------------------------------- */
   const onObjectDestructionError = useCallback((errorInfo) => {
    const releaseUrl = releaseUrlRef.current
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    log({
      type: 'Info',
      attr: {
        error_type: "ObjectDestructionError",
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          portion
        },
        errorInfo
      }
    })
  }, [title, isOnNow, contentGuid, mediaGuid, log])
  /* --------------------------------------------- */
  const onCrash = useCallback((errorInfo, errorType = 'PlaybackGeneric') => {
    let config = errorConfigurationRef.current.filter((config) => {
      return config.type === errorType
    })[0] || {}

      if(!config) {
        config = errorConfigurationRef.current.filter((config) => {
          return config.type === "PlaybackGeneric"
        })[0] || {}
      }
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })

    let stringifiedErrorInfo = null

    try {
      stringifiedErrorInfo = JSON.stringify(errorInfo)
    } catch(e) {

    }

    log({
      type: 'Error',
      attr: {
        error_type: config.type,
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo: stringifiedErrorInfo || errorInfo
      }
    })
  }, [sendAnalytics, title, type, playerFeature.portraitPlayerEnabled, contentOfferId, isOnNow,
    contentGuid, mediaGuid, log, playerStartTime, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onBrowserUnsupported = useCallback((errorInfo) => {
    const errorType = 'PlaybackDeviceUnsupported'
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === errorType
    })[0] || {}
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: errorType,
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
    showError({
      type: errorType,
      value: {
        onPrimaryBtnClick: handleErrorOkClick,
        isPlayerError: true,
        isPortraitPlayer: portraitPlayerRef.current
      },
      errorInfo
    })
  }, [contentGuid, contentOfferId, isOnNow, log, mediaGuid,
    playerFeature.portraitPlayerEnabled, sendAnalytics, playerStartTime,
    title, type, handleErrorOkClick, showError, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onHDCPError = useCallback((errorInfo) => {
    const errorType = 'PlayerHdcpDisplay'
    const config = errorConfigurationRef.current.filter((config) => {
      return config.type === errorType
    })[0] || {}
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const subtitle = subtitleRef.current
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendAnalytics({
      type: '_content.playback',
      action: 'error',
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        progress,
        content_duration: duration,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: subtitle,
        error_code: config.code,
        error_message: config.name,
        recommendation_id: mediaInfo?.recommendation_id,
        asset_id: mediaInfo?.uid,
        portion
      }
    })
    log({
      type: 'Error',
      attr: {
        error_type: errorType,
        error_code: config.code,
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'Error',
          source_url: releaseUrl,
          content_title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          message: config.name,
          player_error_code: config.code,
          recommendation_id: mediaInfo?.recommendation_id,
          asset_id: mediaInfo?.uid,
          portion,
        },
        error_shown:true,
        pageUrl:window.location.href,
        errorInfo
      }
    })
    showError({
      type: errorType,
      value: {
        onPrimaryBtnClick: handleErrorOkClick,
        isPlayerError: true,
        isPortraitPlayer: portraitPlayerRef.current
      },
      errorInfo
    })
  }, [contentGuid, contentOfferId, isOnNow, log, mediaGuid, handleErrorOkClick, playerFeature.portraitPlayerEnabled, playerStartTime, sendAnalytics, showError, title, type, mediaInfo.recommendation_id, mediaInfo.uid])
  /* --------------------------------------------- */
  const onSubtitleChange = useCallback((subtitle) => {
    subtitleRef.current = ((!subtitle || subtitle === 'none') ? 'off' : subtitle)
    const _subtitle = subtitleRef.current
    const duration = parseInt(durationTime.current)
    const progress = parseInt(currentTime.current)
    const portion = (progress / duration) || 0
    const pid = pidRef.current
    const releaseUrl = releaseUrlRef.current
    sendAnalytics({
      type: '_content.subtitles',
      action: ((!subtitle || subtitle === 'none') ? 'off' : 'on'),
      attr: {
        start_time: playerStartTime,
        content_offer_id: contentOfferId,
        content_id: contentGuid,
        item_id: contentGuid,
        content_stream_id: pid,
        content_title: title,
        content_type: isTrailerRef.current ? 'trailer' : type,
        content_duration: duration,
        progress,
        playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
        language_code: _subtitle,
        portion
      }
    })
    log({
      type: 'Info',
      attr: {
        event: 'playback',
        module: 'Player',
        playback: {
          media_type: isOnNow ? 'live' : 'on-demand',
          playback_action: 'subtitles-change',
          source_url: releaseUrl,
          title: title,
          stream_id: mediaGuid,
          content_id: contentGuid,
          content_duration: duration,
          progress,
          language_code: subtitle,
          playback_mode: (playerFeature.portraitPlayerEnabled ? 'portrait' : 'fullscreen'),
          portion
        }
      }
    })
  }, [sendAnalytics, playerStartTime, title, type, contentGuid, mediaGuid, playerFeature.portraitPlayerEnabled, contentOfferId, isOnNow, log])
  /* --------------------------------------------- */
  useEffect(() => {
    const { progress = 0, duration = 0 } = utils.parse(location.search)
    setProgress(Number(progress || 0))
    setDuration(Number(duration || 0))
  }, [location.search])
  /* --------------------------------------------- */
  useEffect(() => {
    currentTime.current = parseInt(progress / 1000)
    durationTime.current = parseInt(duration / 1000)
  }, [progress, duration])
  /* --------------------------------------------- */
  useEffect(() => {
    return () => {
      clearInterval(progressTimer.current)
    }
  }, [onComplete])
  /* --------------------------------------------- */
  useEffect(() => {
    if (!playerRef.current) return
    /* --------------------------------------------- */
    const playerUIConfig = getPlayerUIConfig()
    /* --------------------------------------------- */
    const config = playerUIConfig.nonFullscreen
    let isAutoPlay = isFullPagePlayer
    if (playerFeature.portraitPlayerEnabled) {
      isAutoPlay = !!config.autoPlay
    }
    if (playerFeature.portraitPlayerEnabled || isFullPagePlayer) {
      // setIsAutoPlay(isAutoPlay)
      playerRef.current.autoPlay(isAutoPlay)
    }
    /* --------------------------------------------- */
    setShakaConfig(config.shakaConfig)
  }, [
    getPlayerUIConfig, location.search,
    playerFeature.portraitPlayerEnabled, isFullPagePlayer, isShowError
  ])
  /* --------------------------------------------- */
  useEffect(() => {
    if (isShowError) return
    onFullscreenChange(isFullscreen)
  }, [isFullscreen, onFullscreenChange, isShowError])
  /* --------------------------------------------- */
  const setIsPlayerLoaded = useCallback((isPlayerLoaded) => {
    if (!playerRef.current) return
    if (playerRef.current.setIsPlayerLoaded) return playerRef.current.setIsPlayerLoaded(isPlayerLoaded)
  }, [])
  /* --------------------------------------------- */
  const setPlayerError = useCallback((isPlayerError) => {
    if (!playerRef.current) return
    if (playerRef.current.setPlayerError) return playerRef.current.setPlayerError(isPlayerError)
  }, [])
  /* --------------------------------------------- */
  const autoPlay = useCallback((isAutoPlay) => {
    if (!playerRef.current) return
    if (playerRef.current.autoPlay) {
      // setIsAutoPlay(isAutoPlay)
      return playerRef.current.autoPlay(isAutoPlay)
    }
  }, [])
  /* --------------------------------------------- */
  const setPlayerLoading = useCallback((isPlayerLoading) => {
    if (!playerRef.current) return
    if (playerRef.current.setPlayerLoading) return playerRef.current.setPlayerLoading(isPlayerLoading)
  }, [])
  /* --------------------------------------------- */
  useImperativeHandle(ref, () => {
    return {
      play: onPlaceholderPlayBtnClick, setIsPlayerLoaded, setPlayerError, autoPlay, setPlayerLoading
    }
  })
  /* --------------------------------------------- */
  useEffect(() => {
    if (playerFeature.portraitPlayerEnabled) {
      setPlayerLoaded(false)
    } else {
      const isShowError = (errorData) => {
        return Object.keys(errorData).length > 0 && errorPage.isPlayerError && !errorPage.isPortraitPlayer
      }
      const showError = (
        isShowError(errorPage) ||
        isShowError(errorPopup) ||
        isShowError(errorFullscreen) ||
        isShowError(errorToast) ||
        isShowError(errorSnackbar)
      )
      setShowError(showError)
    }
  }, [errorPage, errorPopup, errorFullscreen, errorToast, errorSnackbar, playerFeature.portraitPlayerEnabled])
  /* --------------------------------------------- */
  return (
    <>
      {isShowError && <ErrorPage />}
      {!isShowError &&
        <PlayerProvider
          onUserPlayClick={onUserPlayClick}
          onUserPauseClick={onUserPauseClick}
          onPlayClick={onPlayClick}
          shakaConfig={shakaConfig}
          onSeek={onSeek}
          onHDCPError={onHDCPError}
          onPlaybackDRMError={onPlaybackDRMError}
          onPlaybackGenericError={onPlaybackGenericError}
          onPlaybackManifestFailed={onPlaybackManifestFailed}
          onRecoverablePlayerError={onRecoverablePlayerError}
          onObjectDestructionError={onObjectDestructionError}
          onBrowserUnsupported={onBrowserUnsupported}
          onPIDChange={onPIDChange}
          onStart={onStart}
          currentPlayerMediaData={currentPlayerMediaDataRef.current}
          setNextEpisode={setNextEpisode}
          abrStartingProfile={playerFeature.abrStartingProfile || []}
          startPos={(progress || 0) / 1000}
          startDuration={(duration || 0) / 1000}
          getReleaseUrl={getReleaseUrl}
          setReleaseUrl={setReleaseUrl}
        >
          <Player
            ref={playerRef} {...{
              assetDetailsForRelatedRailInPlayer,
              thisIsChannel,
              showPlaceholderPlayBtn: playerFeature.portraitPlayerEnabled && (isSportingEvent || isOnNow),
              isPortraitPlayerEnabled: playerFeature.portraitPlayerEnabled,
              smilUrl,
              title,
              subTitle,
              thumbnailImgs,
              seasonUid,
              qualityMapping: playerFeature && playerFeature.qualityMapping,
              baseUrl: appConfig.baseUrl,
              mediaId,
              seriesId,
              uid, 
              type,
              isLivePlayer,
              castConfigPlaybackFormat: playerFeature && playerFeature.castConfig && playerFeature.castConfig.playbackAssetFormat,
              messages: appMessages,
              episodeNum,
              seasonNumber,
              seriesTitle,
              onErrorOkClick: handleErrorOkClick,
              onPlaceholderPlayBtnClick,
              onSubtitleChange,
              onPlay,
              onPause,
              onCurrentTimeChange,
              onComplete,
              onUnload,
              onBuffer,
              onConcurrencyLimitViolation,
              onGeoLocationBlocked,
              onPlayerVpnBlocked,
              onLicenseNotGranted,
              onUnknowFormat,
              onAccessDenied,
              onCrash,
              setPlayerLoading,
              isTrailer: !!trailer,
              allowCasting: playerFeature && playerFeature.allowCasting,
              isPlayerLoaded, setPlayerLoaded,
              bingeCountdownDuration,
              catalogTheme,
              ...playerFeature
            }}
          />
        </PlayerProvider>}
    </>
  )
})
/* --------------------------------------------- */
PlayerContainer.propTypes = {
  /* smilUrl for playback. */
  smilUrl: PropTypes.string,
  /* Title to be displayed in the player  */
  title: PropTypes.string,
  /* Subtitle to be displayed in the player  */
  subtitle: PropTypes.string,
  /* Boolean to identify live player(linear player).  */
  isLivePlayer: PropTypes.bool,
  /* Array of thumbnail images to be displayed over the player  */
  thumbnailImgs: PropTypes.array,
  /* On error popup ok click.  */
  onErrorOkClick: PropTypes.func,
  /* Player configurations like showPlayButton etc..  */
  playerFeature: PropTypes.object,
  /* Player theme.  */
  playerTheme: PropTypes.object,
  /* App messages.  */
  appMessages: PropTypes.object,
  /* To identify the playback is a trailer.  */
  isTrailer: PropTypes.bool
}
/* --------------------------------------------- */
PlayerContainer.defaultProps = {
  smilUrl: '',
  title: '',
  subtitle: '',
  isLivePlayer: false,
  thumbnailImgs: [],
  onErrorOkClick: () => { },
  playerFeature: {},
  playerTheme: {},
  appMessages: {},
  isSportingEvent: false,
  isOnNow: false,
  isTrailer: false
}
/* --------------------------------------------- */
export default withRouterInnerRef(PlayerContainer)
/* --------------------------------------------- */